// The advisor's state as the server sends it, and the pure functions the
// document screen lays it out with.
//
// Three shapes, one per table (Phase 4.5). A comment is four sentences and the
// notes behind them; a note is one claim with its explanation and evidence; the
// evaluation is the one card every call reads. None of them carries a history:
// a call writes the row again rather than appending to it.

import type { DocState, ParagraphId } from "../store/ops";

export interface SourceRef {
  id: string;
  url: string;
  title: string;
  volatile: boolean;
  retrieved_at: string;
  /** A volatile fact read long enough ago to be worth re-checking. */
  stale: boolean;
}

export type CardState = "live" | "dismissed";

export interface Note {
  slug: string;
  /** One falsifiable sentence. The only thing shown while the card is shut. */
  claim: string;
  explanation: string;
  evidence: string;
  anchors: string[];
  sources: SourceRef[];
  state: CardState;
  updated_at: string;
}

/** Problem, question, or idea. "Challenge" is gone: it was the hostile one. */
export type CommentType = "problem" | "question" | "idea";
export type CommentState = "live" | "resolved" | "dismissed";

export interface Comment {
  slug: string;
  anchors: string[];
  type: CommentType;
  /** The whole message in one sentence; all that a shut card shows. */
  what: string;
  why: string;
  how: string;
  example: string;
  reasoning: string;
  /** Slugs of the notes this rests on. May be empty. */
  relevant_notes: string[];
  action: "enable_search" | null;
  state: CommentState;
  dismiss_reason: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

/** The one card every call reads, and the reason they pull one way. */
export interface Evaluation {
  subject: string;
  vision: string;
  style: string;
  goal: string;
  audience: string;
  criteria: string;
  done: string;
  todo: string;
  reasoning: string;
  /** The advisor's estimate. */
  minutes_remaining: number;
  /** Measured from the edit log, never guessed. */
  minutes_spent: number;
  state: CardState;
  updated_at: string;
}

export interface AdvisorState {
  notes: Note[];
  comments: Comment[];
  sources: SourceRef[];
  evaluation: Evaluation | null;
}

export type RunKind = "review" | "evaluation" | "research";
export type RunTrigger = "global" | "selection" | "reassess" | "background";

export interface Run {
  id: string;
  kind: RunKind;
  model: string;
  status: "running" | "done" | "cancelled" | "failed";
  started_at: string;
  ended_at: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  total_prompt_tokens: number;
  search_count: number;
  fetch_count: number;
  cost_usd: number;
  produced: Record<string, unknown>;
  error: { code: string; message: string; retryable?: boolean } | null;
  /** Which surface asked. */
  trigger: RunTrigger;
  /** The comment a reassess is about. */
  slug: string | null;
  /** The paragraphs a selection review is about. */
  anchors: string[];
}

export interface RunStep {
  at: string;
  type: string;
  detail: string;
}

export const EMPTY: AdvisorState = {
  notes: [],
  comments: [],
  sources: [],
  evaluation: null,
};

/** Comments the author has not closed, newest first.
 *
 *  Nothing ranks them any more (D28). Direction comes from the evaluation
 *  card's goal and todo, which every call reads, rather than from an ordering
 *  that independent calls could not agree on anyway.
 */
export function open(comments: Comment[]): Comment[] {
  return comments
    .filter((c) => c.state === "live")
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export function closed(comments: Comment[]): Comment[] {
  return comments
    .filter((c) => c.state === "resolved" || c.state === "dismissed")
    .sort((a, b) => (b.closed_at ?? "").localeCompare(a.closed_at ?? ""));
}

/** Notes the author has not put away. */
export function standing(notes: Note[]): Note[] {
  return notes.filter((n) => n.state === "live");
}

/** Sources behind a card, gathered from the notes it points at and renumbered
 *  for this card, so [1] means the first source shown beneath it. */
export function sourcesFor(comment: Comment, notes: Note[]): SourceRef[] {
  const byslug = new Map(notes.map((n) => [n.slug, n]));
  const out: SourceRef[] = [];
  for (const slug of comment.relevant_notes) {
    for (const source of byslug.get(slug)?.sources ?? []) {
      if (!out.some((s) => s.id === source.id)) out.push(source);
    }
  }
  return out;
}

/** The notes a comment rests on, in the order it named them. */
export function notesFor(comment: Comment, notes: Note[]): Note[] {
  const byslug = new Map(notes.map((n) => [n.slug, n]));
  return comment.relevant_notes.map((slug) => byslug.get(slug)).filter((n): n is Note => !!n);
}

/** A comment as plain text, for the clipboard.
 *
 *  The whole comment, never the example alone: the example is there to show
 *  the author what a change could look like, and a control that lifted it out
 *  on its own would be one step from pasting it into the document (D31).
 */
export function asText(comment: Comment): string {
  return [
    comment.what,
    `Why: ${comment.why}`,
    `How: ${comment.how}`,
    `For example: ${comment.example}`,
  ].join("\n");
}

/** The paragraph a comment sits under: the last of its anchors by position
 *  in the document. Null for a comment about the whole text or one whose
 *  anchors are all gone. */
export function lastAnchor(comment: Comment, order: ParagraphId[]): ParagraphId | null {
  let best: ParagraphId | null = null;
  let bestAt = -1;
  for (const id of comment.anchors) {
    const at = order.indexOf(id);
    if (at > bestAt) {
      bestAt = at;
      best = id;
    }
  }
  return best;
}

function byNewest(a: Comment, b: Comment): number {
  return b.updated_at.localeCompare(a.updated_at);
}

/** Comments grouped under the paragraph each one sits under (design §7.2). */
export function trayIndex(comments: Comment[], order: ParagraphId[]): Map<ParagraphId, Comment[]> {
  const out = new Map<ParagraphId, Comment[]>();
  for (const comment of [...comments].sort(byNewest)) {
    const at = lastAnchor(comment, order);
    if (at === null) continue;
    const list = out.get(at);
    if (list) list.push(comment);
    else out.set(at, [comment]);
  }
  return out;
}

/** Comments with no paragraph to sit under. They go at the top, under the
 *  evaluation card. */
export function docScoped(comments: Comment[]): Comment[] {
  return comments.filter((c) => c.anchors.length === 0).sort(byNewest);
}

/** A selection split into the runs of neighbouring paragraphs it covers, in
 *  document order.
 *
 *  A review is about one passage, so a selection with a gap in it is two
 *  questions rather than one badly-shaped one, and each gets its own call and
 *  its own answer. */
export function contiguousGroups(
  selection: ParagraphId[],
  order: ParagraphId[],
): ParagraphId[][] {
  const positions = selection
    .map((id) => order.indexOf(id))
    .filter((at) => at >= 0)
    .sort((a, b) => a - b);
  const groups: ParagraphId[][] = [];
  let run: ParagraphId[] = [];
  let last = -2;
  for (const at of positions) {
    if (at !== last + 1 && run.length > 0) {
      groups.push(run);
      run = [];
    }
    run.push(order[at]!);
    last = at;
  }
  if (run.length > 0) groups.push(run);
  return groups;
}

/** What the tray's Review button would be about, as passages (design §6.2).
 *
 *  A selection made inside a paragraph is rounded up to that paragraph and
 *  never rejected, and it comes first: it is the most recent thing the author
 *  did to say what they mean. Then the gutter selection, which may cover
 *  several paragraphs. An empty result means there is nothing selected — and
 *  the tray is not shown at all.
 */
export function selectionScope(
  textSelection: ParagraphId | null,
  rows: ParagraphId[],
  doc: DocState,
): ParagraphId[][] {
  if (textSelection && textSelection in doc.text) return [[textSelection]];
  return contiguousGroups(
    rows.filter((id) => id in doc.text),
    doc.order,
  );
}

/** How far along the author is: spent / (spent + remaining), as a percentage. */
export function score(evaluation: Evaluation): number {
  const total = evaluation.minutes_spent + evaluation.minutes_remaining;
  if (total <= 0) return 0;
  return Math.round((100 * evaluation.minutes_spent) / total);
}
